/**
 * Competition Manager Service
 * Handles 3-day competition cycles, a 1-day break between them,
 * registration tracking, and timer management.
 */

import { getFirestore, doc, getDoc, setDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { app } from '../firebase';

const db = getFirestore(app);
const CYCLE_DURATION = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds
const BREAK_DURATION = 1 * 24 * 60 * 60 * 1000; // 1 day in milliseconds

class CompetitionManager {
  constructor() {
    this.currentCycle = null;
    this._transitioning = false; // guards against overlapping Firestore writes
    this.initializeCycle();
  }

  /**
   * Initialize or catch up the current competition cycle/break phase
   */
  async initializeCycle() {
    try {
      const cycleDoc = await getDoc(doc(db, 'competition', 'currentCycle'));

      if (cycleDoc.exists()) {
        const cycleData = cycleDoc.data();
        const now = Date.now();
        // Older documents (written before the break feature existed)
        // won't have a phase field yet — treat them as 'competing'.
        const normalized = { ...cycleData, phase: cycleData.phase || 'competing' };

        if (now >= normalized.endTime) {
          this.currentCycle = normalized;
          if (normalized.phase === 'break') {
            await this.startNewCycle();
          } else {
            await this.startBreak();
          }
        } else {
          this.currentCycle = normalized;
        }
      } else {
        await this.startNewCycle();
      }
    } catch (error) {
      console.error('Error initializing competition cycle:', error);
      this.initializeLocalCycle();
    }
  }

  /**
   * Start a new 3-day competition cycle
   */
  async startNewCycle() {
    if (this._transitioning) return;
    this._transitioning = true;

    const now = Date.now();
    const cycleId = `cycle_${now}`;

    const newCycle = {
      id: cycleId,
      phase: 'competing',
      startTime: now,
      endTime: now + CYCLE_DURATION,
      status: 'active',
      participantCount: 0,
      createdAt: now
    };

    try {
      // Save to Firestore
      await setDoc(doc(db, 'competition', 'currentCycle'), newCycle);

      // Also save to localStorage as backup
      localStorage.setItem('competitionStart', now.toString());
      localStorage.setItem('currentCycleId', cycleId);
      localStorage.setItem('competitionPhase', 'competing');

      this.currentCycle = newCycle;

      console.log('New competition cycle started:', cycleId);
    } catch (error) {
      console.error('Error starting new cycle:', error);
      // Fallback to localStorage
      this.initializeLocalCycle();
    } finally {
      this._transitioning = false;
    }
  }

  /**
   * End the current competition and start the 1-day break before the next one
   */
  async startBreak() {
    if (this._transitioning) return;
    this._transitioning = true;

    const now = Date.now();
    const baseCycle = this.currentCycle || {};

    const breakCycle = {
      ...baseCycle,
      phase: 'break',
      startTime: now,
      endTime: now + BREAK_DURATION,
      status: 'break'
    };

    try {
      await setDoc(doc(db, 'competition', 'currentCycle'), breakCycle);

      localStorage.setItem('competitionPhase', 'break');
      localStorage.setItem('breakStart', now.toString());

      this.currentCycle = breakCycle;

      // Now that the competition is actually over, move its ideas
      // into Rewarded Ideas during the break window.
      await this.moveCompetitionToRewards();

      console.log('Competition ended, break started:', breakCycle.id);
    } catch (error) {
      console.error('Error starting break:', error);
    } finally {
      this._transitioning = false;
    }
  }

  /**
   * Get current cycle information
   */
  getCurrentCycle() {
    return this.currentCycle;
  }

  /**
   * Is the platform currently between competitions?
   */
  isBreakTime() {
    return this.currentCycle?.phase === 'break';
  }

  /**
   * Calculate time remaining in the current phase (competing or break)
   */
  getTimeRemaining() {
    if (!this.currentCycle) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, phase: 'competing' };
    }

    const now = Date.now();
    const remaining = this.currentCycle.endTime - now;
    const phase = this.currentCycle.phase || 'competing';

    if (remaining <= 0) {
      // Phase has ended — kick off the next one in the background.
      // Return an immediate "just started" reading so the UI doesn't
      // flicker while the Firestore write for the transition completes.
      if (phase === 'break') {
        this.startNewCycle();
        return { days: 3, hours: 0, minutes: 0, seconds: 0, phase: 'competing' };
      } else {
        this.startBreak();
        return { days: 1, hours: 0, minutes: 0, seconds: 0, phase: 'break' };
      }
    }

    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((remaining % (60 * 1000)) / 1000);

    return { days, hours, minutes, seconds, phase };
  }

  /**
   * Register user for current competition
   */
  async registerUser(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        competitionRegistered: true,
        registeredCycleId: this.currentCycle?.id,
        registrationTime: Date.now()
      });

      // Update participant count
      if (this.currentCycle) {
        const cycleRef = doc(db, 'competition', 'currentCycle');
        await updateDoc(cycleRef, {
          participantCount: (this.currentCycle.participantCount || 0) + 1
        });
        this.currentCycle.participantCount++;
      }

      return true;
    } catch (error) {
      console.error('Error registering user:', error);
      return false;
    }
  }

  /**
   * Check if the user has already posted/registered in the CURRENT
   * competition cycle. Because registeredCycleId is stamped with the
   * cycle's id at registration time, this naturally resets every time
   * startNewCycle() generates a fresh id for the next competition.
   */
  async isUserRegistered(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.competitionRegistered &&
               userData.registeredCycleId === this.currentCycle?.id;
      }
      return false;
    } catch (error) {
      console.error('Error checking user registration:', error);
      return false;
    }
  }

  /**
   * Move competition ideas to rewarded ideas collection
   */
  async moveCompetitionToRewards() {
    try {
      const competitionSnapshot = await getDocs(collection(db, 'competitionIdeas'));

      for (const docSnapshot of competitionSnapshot.docs) {
        const ideaData = docSnapshot.data();

        // Add to rewarded ideas collection
        await setDoc(doc(db, 'rewardedIdeas', docSnapshot.id), {
          ...ideaData,
          rewardedAt: Date.now(),
          originalCompetitionId: docSnapshot.id,
          status: 'rewarded'
        });
      }

      // Note: In production, you might want to archive instead of delete
      console.log('Competition ideas moved to rewards');
    } catch (error) {
      console.error('Error moving competition to rewards:', error);
    }
  }

  /**
   * Fallback to localStorage-based cycle management (used only if
   * Firestore reads/writes fail).
   */
  initializeLocalCycle() {
    let competitionStart = localStorage.getItem('competitionStart');
    let phase = localStorage.getItem('competitionPhase') || 'competing';

    if (!competitionStart) {
      competitionStart = Date.now();
      localStorage.setItem('competitionStart', competitionStart.toString());
    } else {
      competitionStart = parseInt(competitionStart, 10);
    }

    const duration = phase === 'break' ? BREAK_DURATION : CYCLE_DURATION;

    this.currentCycle = {
      id: `local_${competitionStart}`,
      phase,
      startTime: competitionStart,
      endTime: competitionStart + duration,
      status: phase === 'break' ? 'break' : 'active',
      participantCount: 0,
      isLocal: true
    };
  }

  /**
   * Get competition statistics
   */
  async getCompetitionStats() {
    try {
      const [competitionSnapshot, rewardedSnapshot] = await Promise.all([
        getDocs(collection(db, 'competitionIdeas')),
        getDocs(collection(db, 'rewardedIdeas'))
      ]);

      return {
        activeSubmissions: competitionSnapshot.size,
        totalRewarded: rewardedSnapshot.size,
        currentParticipants: this.currentCycle?.participantCount || 0,
        cycleStatus: this.currentCycle?.status || 'unknown'
      };
    } catch (error) {
      console.error('Error getting competition stats:', error);
      return {
        activeSubmissions: 0,
        totalRewarded: 0,
        currentParticipants: 0,
        cycleStatus: 'error'
      };
    }
  }
}

// Export singleton instance
const competitionManager = new CompetitionManager();
export default competitionManager;